import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Confetti from "react-confetti";
import { dryrun } from "@permaweb/aoconnect";
import { connect } from "@permaweb/aoconnect";
import { AOProcess } from "@ar.io/sdk/web";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Check, 
  Wallet, 
  Link2, 
  MessageSquare,
  ChevronRight,
  AlertCircle,
  Loader2,
  Shield,
  User,
  X
} from "lucide-react";
import { SiX, SiGithub } from "@icons-pack/react-simple-icons";
import Profile from "../components/Profile";
import { useArns } from '../hooks/useArns';

// --- Constants ---
const API_URL = import.meta.env.VITE_API_URL?.trim() || "https://api.dev.amportal.ai";
const VERIFY_LINK_ENDPOINT = `${API_URL}/api/verify-link`;

// Custom AO with CU
const customAo = connect({
  MODE: 'legacy' as const,
  CU_URL: 'https://cu.ardrive.io',
});
const customProcess = new AOProcess({
  processId: 'qNvAoz0TgcH7DMg8BCVn8jF32QH5L6T29VjHxhHqqGE',
  ao: customAo,
});

// --- Custom Hook for Wallet Detection ---
const useArConnectDetection = () => {
  const [walletDetected, setDetected] = useState(false);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    let timeout: NodeJS.Timeout;

    const flagDetected = () => {
      if (window.arweaveWallet && typeof window.arweaveWallet.connect === "function") {
        setDetected(true);
        clearInterval(interval);
        clearTimeout(timeout);
      }
    };

    flagDetected();

    if (!walletDetected) {
      interval = setInterval(flagDetected, 500);
      timeout = setTimeout(() => {
        clearInterval(interval);
      }, 10000);
    }

    window.addEventListener("arweaveWalletLoaded", flagDetected);

    return () => {
      if (interval) clearInterval(interval);
      if (timeout) clearTimeout(timeout);
      window.removeEventListener("arweaveWalletLoaded", flagDetected);
    };
  }, [walletDetected]);

  return walletDetected;
};

// --- Main App Component ---
function AmVerify() {
  // State management
  const [currentStep, setCurrentStep] = useState(0);
  const [verificationToken, setVerificationToken] = useState<string | null>(null);
  const [linkDiscordId, setLinkDiscordId] = useState<string | null>(null);
  const [linkGuildId, setLinkGuildId] = useState<string | null>(null);
  const [checkingLink, setCheckingLink] = useState(true);
  const [isLinkValid, setIsLinkValid] = useState(false);
  const [shouldCheckLink, setShouldCheckLink] = useState(false);
  const walletDetected = useArConnectDetection();
  const [walletConnected, setWalletConnected] = useState(false);
  const [address, setAddress] = useState(() => localStorage.getItem("walletAddress") || "");
  const [publicKey, setPublicKey] = useState(() => localStorage.getItem("publicKey") || "");
  const [discordId, setDiscordId] = useState(() => localStorage.getItem("discordId") || "");
  const [githubId, setGithubId] = useState(() => localStorage.getItem("githubId") || "");
  const [xId, setXId] = useState(() => localStorage.getItem("xId") || "");
  const [guildId, setGuildId] = useState(() => localStorage.getItem("guildId") || "");
  const [status, setStatus] = useState("Loading...");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [lastVerifiedRoles, setLastVerifiedRoles] = useState<string[] | null>(null);
  const [linkValidated, setLinkValidated] = useState(false);
  const [showProfile, setShowProfile] = useState(false);

  // ArNS hook - simplified for verification flow
  const {
    currentPrimaryName,
    antOptions,
    loading: loadingArns,
    error: arnsError
  } = useArns(walletConnected ? address : null, customProcess);

  // Determine current onboarding step
  useEffect(() => {
    if (!walletConnected) {
      setCurrentStep(0);
    } else if (!discordId) {
      setCurrentStep(1);
    } else if (!githubId && !xId) {
      setCurrentStep(2);
    } else {
      setCurrentStep(3);
    }
  }, [walletConnected, discordId, githubId, xId]);

  function parseUrlParams() {
    const params = new URLSearchParams(window.location.search);
    return {
      discordId: params.get("discordId"),
      guildId: params.get("guildId"),
      token: params.get("token"),
      githubId: params.get("githubId"),
      xId: params.get("xId"),
    };
  }

  async function fetchDiscordIdByWallet(walletAddress: string) {
    if (!walletAddress) return null;

    const nonce = Math.random().toString(36).slice(2);
    try {
      const res = await dryrun({
        process: process.env.REACT_APP_AO_PROCESS_ID!,
        tags: [
          { name: "Action", value: "Get-User-By-Wallet" },
          { name: "Wallet-Address", value: walletAddress },
          { name: "Nonce", value: nonce },
        ],
      });

      const msg = res?.Messages?.[0]?.Data
        ? JSON.parse(res.Messages[0].Data)
        : { Success: false };

      return msg.Success ? msg.userId : null;
    } catch (e) {
      console.warn("[fetchDiscordIdByWallet] lookup failed:", e);
      return null;
    }
  }

  const saveToAO = useCallback(
    async (platform: string, id: string, currentDiscordId: string) => {
      const effectiveDiscordId = currentDiscordId || discordId;
      if (!effectiveDiscordId) {
        setError("Discord ID missing, cannot link account. Please link Discord first.");
        return;
      }
      const currentStateId = platform === "github" ? githubId : xId;
      if (id === currentStateId) {
        return;
      }
      setLoading(true);
      setStatus(`Linking ${platform === "github" ? "GitHub" : "X"} account...`);
      setError("");
      try {
        await axios.post(`${API_URL}/api/link-${platform}`, {
          discordId: effectiveDiscordId,
          [platform === "github" ? "githubId" : "xId"]: id,
        });
        setStatus(`${platform === "github" ? "GitHub" : "X"} linked successfully!`);
        if (platform === "github") {
          setGithubId(id);
          localStorage.setItem("githubId", id);
        } else {
          setXId(id);
          localStorage.setItem("xId", id);
        }
      } catch (err: any) {
        const errorMsg = err.response?.data?.message || err.message || "Unknown error";
        setError(`Failed to link ${platform}: ${errorMsg}`);
        setStatus(`Failed to link ${platform}. Please try again.`);
      } finally {
        setLoading(false);
      }
    },
    [discordId, githubId, xId]
  );

  // Initialize from URL params
  useEffect(() => {
    const urlParams = parseUrlParams();
    let urlContainsLinkData = false;

    if (urlParams.token) {
      setVerificationToken(urlParams.token);
      setLinkDiscordId(urlParams.discordId);
      setLinkGuildId(urlParams.guildId);
      setShouldCheckLink(true);
      setStatus("Preparing verification...");
      urlContainsLinkData = true;
    } else {
      setCheckingLink(false);
      const storedDiscordId = localStorage.getItem("discordId");
      const storedGuildId = localStorage.getItem("guildId");
      if (storedDiscordId && storedGuildId) {
        setStatus("Please connect your wallet.");
      } else {
        setStatus("Please connect your wallet and link Discord to verify.");
      }
    }

    if (urlParams.discordId) {
      setDiscordId(urlParams.discordId);
      localStorage.setItem("discordId", urlParams.discordId);
    } else {
      const storedDiscordId = localStorage.getItem("discordId");
      if (storedDiscordId) setDiscordId(storedDiscordId);
    }

    if (urlParams.guildId) {
      setGuildId(urlParams.guildId);
      localStorage.setItem("guildId", urlParams.guildId);
    } else {
      const storedGuildId = localStorage.getItem("guildId");
      if (storedGuildId) setGuildId(storedGuildId);
    }

    if (urlParams.githubId && urlParams.discordId) {
      saveToAO("github", urlParams.githubId, urlParams.discordId);
      urlContainsLinkData = true;
    }
    if (urlParams.xId && urlParams.discordId) {
      saveToAO("x", urlParams.xId, urlParams.discordId);
      urlContainsLinkData = true;
    }

    const storedWalletAddress = localStorage.getItem("walletAddress");
    const storedPublicKey = localStorage.getItem("publicKey");
    if (storedWalletAddress) {
      setAddress(storedWalletAddress);
      setPublicKey(storedPublicKey || "");
      setWalletConnected(true);
    }

    if (urlContainsLinkData) {
      setTimeout(() => {
        window.history.replaceState({}, document.title, window.location.pathname);
      }, 800);
    }
  }, [saveToAO]);

  // Validate verification link
  useEffect(() => {
    if (linkValidated || !shouldCheckLink || !verificationToken || !linkDiscordId || !linkGuildId) {
      return;
    }
    setLinkValidated(true);
    setCheckingLink(true);
    setStatus("Validating verification link...");
    setError("");

    (async () => {
      try {
        const response = await axios.get(VERIFY_LINK_ENDPOINT, {
          params: {
            discordId: linkDiscordId,
            guildId: linkGuildId,
            token: verificationToken,
          },
          timeout: 10000,
        });

        const data = response.data;
        if (data.success) {
          setIsLinkValid(true);
          setLinkValidated(true);
          setStatus(walletDetected ? "Link validated. Please connect your wallet." : "Link validated. Detecting wallet...");
        } else {
          setIsLinkValid(false);
          setError(`Invalid or expired verification link: ${data.message}`);
          setStatus("Verification link error. Please request a new link.");
        }
      } catch (err: any) {
        setIsLinkValid(false);
        setLinkValidated(true);
        const errorMsg = err.code === "ECONNABORTED" ? "API timeout. Please try again." : err.message || "Unknown error";
        setError(`Error validating link: ${errorMsg}`);
        setStatus("Verification link error.");
      } finally {
        setCheckingLink(false);
        setShouldCheckLink(false);
      }
    })();
  }, [linkValidated, shouldCheckLink, verificationToken, linkDiscordId, linkGuildId, walletDetected]);

  // Check for existing Discord link when wallet connects
  useEffect(() => {
    if (!walletConnected || !address || discordId) return;
    (async () => {
      const foundId = await fetchDiscordIdByWallet(address);
      if (foundId) {
        setDiscordId(foundId);
        localStorage.setItem("discordId", foundId);
        setStatus("Wallet recognised. Welcome back 👋");
      } else {
        setStatus("Wallet recognised but not linked. Please link Discord to verify.");
      }
    })();
  }, [walletConnected, address, discordId]);

  const checkWalletConnection = useCallback(async () => {
    if (verificationToken !== null && !isLinkValid) return false;
    if (!window.arweaveWallet) {
      console.log("[checkWalletConnection] Wallet interface not available.");
      return false;
    }
    if (walletConnected && address) return true;

    console.log("[checkWalletConnection] Performing silent check for active connection.");
    try {
      if (typeof window.arweaveWallet.getActiveAddress !== 'function') {
        console.log("[checkWalletConnection] getActiveAddress method not available");
        return false;
      }
      
      const currentAddress = await window.arweaveWallet.getActiveAddress();
      if (currentAddress) {
        let currentPublicKey = "";
        try {
          if (typeof window.arweaveWallet.getActivePublicKey === 'function') {
            currentPublicKey = await window.arweaveWallet.getActivePublicKey();
          }
        } catch (pkErr) {
          console.warn("[checkWalletConnection] Could not get public key during silent check:", pkErr);
        }
        
        console.log("[checkWalletConnection] Active connection found, updating state.");
        setAddress(currentAddress);
        setPublicKey(currentPublicKey);
        setWalletConnected(true);
        localStorage.setItem("walletAddress", currentAddress);
        localStorage.setItem("publicKey", currentPublicKey);
        return true;
      }
      
      if (walletConnected) {
        console.log("[checkWalletConnection] No active address, correcting to disconnected state.");
        setWalletConnected(false);
        setAddress("");
        setPublicKey("");
        localStorage.removeItem("walletAddress");
        localStorage.removeItem("publicKey");
      }
      return false;
    } catch (err: any) {
      console.warn("[checkWalletConnection] Silent check failed (likely not connected):", err?.message || "Unknown error");
      if (walletConnected) {
        setWalletConnected(false);
        setAddress("");
        setPublicKey("");
        localStorage.removeItem("walletAddress");
        localStorage.removeItem("publicKey");
      }
      return false;
    }
  }, [verificationToken, isLinkValid, walletConnected, address]);

  // Sync wallet connection state when wallet is detected
  useEffect(() => {
    if (walletDetected) {
      console.log("[walletDetection] Wallet detected, performing silent connection check.");
      checkWalletConnection();
    }
  }, [walletDetected, checkWalletConnection]);

  const connectWallet = useCallback(async () => {
    if (verificationToken !== null && !isLinkValid) {
      setError("Cannot connect wallet: Verification link is invalid or expired.");
      setStatus("Please request a new link from Discord.");
      return;
    }
    
    if (!walletDetected || !window.arweaveWallet) {
      setError("Wallet not detected. Please install ArConnect.");
      setStatus("Wallet detection failed.");
      return;
    }
    
    if (await checkWalletConnection()) {
      setStatus("Wallet already connected.");
      console.log("[connectWallet] Wallet already connected, skipping prompt.");
      return;
    }

    console.log("[connectWallet] Prompting for wallet connection.");
    setLoading(true);
    setStatus("Connecting wallet...");
    setError("");
    
    try {
      if (typeof window.arweaveWallet.connect !== 'function') {
        throw new Error("ArConnect wallet interface is not properly loaded. Please refresh the page and try again.");
      }

      console.log("[connectWallet] Calling arweaveWallet.connect...");
      
      const permissions = [
        "ACCESS_ADDRESS",
        "SIGNATURE",
        "ACCESS_PUBLIC_KEY",
        "ACCESS_TOKENS",
        "ACCESS_ALL_ADDRESSES",
        "SIGN_TRANSACTION",
        "DISPATCH",
      ];
      
      await window.arweaveWallet.connect(permissions, { 
        name: "amPortal Verification",
        logo: "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg=="
      });

      console.log("[connectWallet] Connect call successful, getting address...");
      const addr = await window.arweaveWallet.getActiveAddress();
      
      if (!addr) {
        throw new Error("Failed to retrieve wallet address after connection. Please try reconnecting.");
      }

      console.log("[connectWallet] Getting public key...");
      let pk = "";
      try {
        pk = await window.arweaveWallet.getActivePublicKey();
      } catch (pkErr) {
        console.warn("[connectWallet] Could not get public key, continuing without it:", pkErr);
      }

      console.log("[connectWallet] Connection successful, address:", addr);
      setAddress(addr);
      setPublicKey(pk || "");
      setWalletConnected(true);
      localStorage.setItem("walletAddress", addr);
      localStorage.setItem("publicKey", pk || "");
      setStatus("Wallet connected successfully!");
      setError("");

      // The useArns hook will automatically fetch when address changes

    } catch (connectErr: any) {
      console.error("[connectWallet] Connection error:", connectErr);

      let errMsg = "Unknown connection error occurred.";
      
      if (connectErr && typeof connectErr === 'object') {
        if (connectErr.message) {
          if (connectErr.message.includes("User rejected") || 
              connectErr.message.includes("denied") || 
              connectErr.message.includes("cancelled")) {
            errMsg = "Connection request was rejected by user.";
          } else if (connectErr.message.includes("not installed") || 
                     connectErr.message.includes("not found")) {
            errMsg = "ArConnect extension not found. Please install ArConnect and refresh the page.";
          } else if (connectErr.message.includes("timeout") || 
                     connectErr.message.includes("timed out")) {
            errMsg = "Connection timed out. Please try again.";
          } else if (connectErr.message.includes("permission") || 
                     connectErr.message.includes("Permission")) {
            errMsg = "Permission denied. Please allow the requested permissions and try again.";
          } else if (connectErr.message.includes("Input is not a valid permission")) {
            errMsg = "Invalid permission requested. Please update ArConnect extension.";
          } else {
            errMsg = connectErr.message;
          }
        } else if (connectErr.name) {
          errMsg = `Connection error: ${connectErr.name}`;
        } else if (connectErr.code) {
          errMsg = `Connection error (code: ${connectErr.code})`;
        }
      } else if (typeof connectErr === 'string') {
        errMsg = connectErr;
      }

      setError(`Failed to connect wallet: ${errMsg}`);
      setStatus("Wallet connection failed.");
      setWalletConnected(false);
      setAddress("");
      setPublicKey("");
      localStorage.removeItem("walletAddress");
      localStorage.removeItem("publicKey");
    } finally {
      setLoading(false);
    }
  }, [walletDetected, verificationToken, isLinkValid, checkWalletConnection]);

  const disconnectWallet = useCallback(async () => {
    console.log("[disconnectWallet] Attempting disconnection.");
    setStatus("Disconnecting...");
    try {
      if (window.arweaveWallet && typeof window.arweaveWallet.disconnect === "function") {
        await window.arweaveWallet.disconnect();
      }
      setWalletConnected(false);
      setAddress("");
      setPublicKey("");
      setDiscordId("");
      setGuildId("");
      setGithubId("");
      setXId("");
      setVerificationToken(null);
      setLinkDiscordId(null);
      setLinkGuildId(null);
      setIsLinkValid(false);
      setCheckingLink(false);
      setStatus("Wallet disconnected. Connect to proceed.");
      setError("");
      setShowSuccess(false);
      localStorage.removeItem("walletAddress");
      localStorage.removeItem("publicKey");
      localStorage.removeItem("discordId");
      localStorage.removeItem("githubId");
      localStorage.removeItem("xId");
      localStorage.removeItem("guildId");
    } catch (err: any) {
      console.error("[disconnectWallet] Disconnection error:", err);
      setError(`Wallet disconnection failed: ${err.message || "Unknown error"}`);
      setStatus("Disconnect failed.");
    }
  }, []);

  const linkDiscord = useCallback(() => {
    if (verificationToken !== null && !isLinkValid) {
      setError("Cannot link Discord: Verification link is invalid or expired.");
      setStatus("Please request a new link from Discord.");
      return;
    }
    if (!guildId) {
      setError("Linking Discord requires a server context. Please start from a Discord server link.");
      return;
    }
    if (discordId && (verificationToken === null || isLinkValid)) {
      setStatus("Discord already linked.");
      return;
    }

    setLoading(true);
    setError("");
    const redirectUrl = `${API_URL}/auth/discord?guildId=${guildId}${discordId ? `&discordId=${discordId}` : ""}`;
    window.location.href = redirectUrl;
  }, [guildId, discordId, verificationToken, isLinkValid]);

  const linkGithub = useCallback(() => {
    if (verificationToken !== null && !isLinkValid) {
      setError("Cannot link GitHub: Verification link is invalid or expired.");
      setStatus("Please request a new link from Discord.");
      return;
    }
    if (!discordId) {
      setError("Please link Discord first.");
      setStatus("Link Discord first.");
      return;
    }

    setLoading(true);
    setError("");
    const redirectUrl = `${API_URL}/auth/github?discordId=${discordId}${guildId ? `&guildId=${guildId}` : ""}${verificationToken ? `&token=${verificationToken}` : ""}`;
    window.location.href = redirectUrl;
  }, [discordId, guildId, verificationToken, isLinkValid]);

  const linkX = useCallback(() => {
    if (verificationToken !== null && !isLinkValid) {
      setError("Cannot link X: Verification link is invalid or expired.");
      setStatus("Please request a new link from Discord.");
      return;
    }
    if (!discordId) {
      setError("Please link Discord first.");
      setStatus("Link Discord first.");
      return;
    }

    setLoading(true);
    setError("");
    const redirectUrl = `${API_URL}/auth/x?discordId=${encodeURIComponent(discordId)}${guildId ? `&guildId=${guildId}` : ""}${verificationToken ? `&token=${verificationToken}` : ""}`;
    window.location.href = redirectUrl;
  }, [discordId, guildId, verificationToken, isLinkValid]);

  const verify = useCallback(async () => {
    if (verificationToken !== null && !isLinkValid) {
      setError("Cannot verify: Verification link is invalid or expired. Please request a new link from Discord.");
      setStatus("Verification failed.");
      return;
    }
    if (!(await checkWalletConnection())) {
      setError("Wallet not connected. Please connect your wallet first.");
      setStatus("Wallet connection required.");
      return;
    }
    if (!discordId) {
      setError("Discord account not linked. Please link Discord first.");
      setStatus("Link Discord required.");
      return;
    }
    if (!guildId) {
      setError("Verification requires a specific server context. Please start verification from a Discord server link.");
      setStatus("Server context missing.");
      return;
    }

    setLoading(true);
    setStatus("Preparing verification...");
    setError("");

    try {
      setStatus("Requesting verification challenge...");
      const nonceRes = await axios.get(`${API_URL}/api/nonce/${discordId}`, { params: { guildId } });
      const nonce = nonceRes.data.nonce;
      if (!nonce) {
        throw new Error("Failed to retrieve verification challenge (nonce).");
      }

      setStatus("Please sign the message in your wallet...");
      if (!window.arweaveWallet?.signMessage) {
        throw new Error("Wallet does not support signing messages.");
      }
      const encodedNonce = new TextEncoder().encode(nonce);
      const signatureBuffer = await window.arweaveWallet.signMessage(encodedNonce);
      const signatureBase64 = btoa(String.fromCharCode(...new Uint8Array(signatureBuffer)));

      const verifyPayload = {
        discordId,
        walletAddress: address,
        signature: signatureBase64,
        publicKey,
        nonce,
        antName: currentPrimaryName,
        antProcessId: "", // Will be looked up by server
        guildId,
      };

      setStatus("Submitting verification...");
      const verifyRes = await axios.post(`${API_URL}/api/verify`, verifyPayload);

      if (verifyRes.data.success) {
        setStatus("Verification complete!");
        setError("");
        setLastVerifiedRoles(verifyRes.data.roles || []);
        setShowSuccess(true);
      } else {
        const errorMsg = verifyRes.data.message || "Unknown error";
        if (errorMsg.includes("Invalid nonce")) {
          setError("Verification failed: Invalid or expired challenge. Please try again.");
        } else if (errorMsg.includes("Invalid signature")) {
          setError("Verification failed: Signature mismatch. Please ensure you signed with the correct wallet.");
        } else if (errorMsg.includes("Wallet already linked")) {
          setError("Verification failed: This wallet is already linked to a different Discord account.");
        } else if (errorMsg.includes("Missing required parameters")) {
          setError("Verification failed: Missing server context or other required data.");
        } else {
          setError(`Verification failed: ${errorMsg}`);
        }
        setStatus("Verification failed.");
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || "Unknown error";
      setError(`Verification failed: ${errorMsg}.`);
      setStatus("Verification failed.");
    } finally {
      setLoading(false);
    }
  }, [
    verificationToken,
    isLinkValid,
    checkWalletConnection,
    discordId,
    guildId,
    address,
    publicKey,
    currentPrimaryName,
  ]);

  const steps = [
    { icon: Wallet, label: "Connect Wallet", complete: walletConnected },
    { icon: MessageSquare, label: "Link Discord", complete: !!discordId },
    { icon: Link2, label: "Link Socials", complete: !!githubId || !!xId },
    { icon: Shield, label: "Verify Holdings", complete: false }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-950 via-black to-green-900 text-white overflow-hidden">
      {/* Background Effects */}
      <div className="fixed inset-0 bg-[url('data:image/svg+xml,%3Csvg width=%2260%22 height=%2260%22 viewBox=%220 0 60 60%22 xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg fill=%22none%22 fill-rule=%22evenodd%22%3E%3Cg fill=%22%2300ff00%22 fill-opacity=%220.03%22%3E%3Cpath d=%22M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-20"></div>
      
      {/* Header */}
      <motion.header 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative z-10 p-6 flex justify-between items-center"
      >
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
            <span className="text-black font-bold text-xl">$</span>
          </div>
          <h1 className="text-2xl font-bold">Verification</h1>
        </div>
        {walletConnected && (
          <motion.button
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={() => setShowProfile(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-green-500/20 hover:bg-green-500/30 rounded-lg transition-colors"
          >
            <User className="w-4 h-4" />
            <span className="text-sm">Profile</span>
          </motion.button>
        )}
      </motion.header>

      {/* Main Content */}
      <main className="relative z-10 max-w-6xl mx-auto px-6 py-12">
        {/* Progress Steps */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mb-12"
        >
          <div className="flex items-center justify-center space-x-2 mb-8">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <React.Fragment key={index}>
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: index * 0.1 }}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-full ${
                      step.complete 
                        ? 'bg-green-500/20 border border-green-500' 
                        : currentStep === index 
                        ? 'bg-green-500/10 border border-green-500/50'
                        : 'bg-white/5 border border-white/10'
                    }`}
                  >
                    <Icon className={`w-4 h-4 ${step.complete ? 'text-green-500' : 'text-white/50'}`} />
                    <span className={`text-sm ${step.complete ? 'text-green-500' : 'text-white/50'}`}>
                      {step.label}
                    </span>
                    {step.complete && <Check className="w-4 h-4 text-green-500" />}
                  </motion.div>
                  {index < steps.length - 1 && (
                    <ChevronRight className="w-4 h-4 text-white/20" />
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </motion.div>

        {/* Error Display */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg flex items-start space-x-3"
            >
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-400">{error}</p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Main Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 p-4 md:p-8"
        >
          {/* Dynamic Content Based on Step */}
          {currentStep === 0 && (
            <div className="text-center">
              <Wallet className="w-16 h-16 mx-auto mb-4 text-green-500" />
              <h2 className="text-2xl font-bold mb-2">Connect Your Wallet</h2>
              <p className="text-white/60 mb-6">
                Connect your Arweave wallet to begin the verification process
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={connectWallet}
                disabled={!walletDetected || loading}
                className="px-8 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 disabled:from-gray-600 disabled:to-gray-700 rounded-lg font-semibold transition-all flex items-center space-x-2 mx-auto"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Connecting...</span>
                  </>
                ) : walletDetected ? (
                  <>
                    <Wallet className="w-5 h-5" />
                    <span>Connect Wallet</span>
                  </>
                ) : (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Detecting Wallet...</span>
                  </>
                )}
              </motion.button>
              {!walletDetected && (
                <p className="text-sm text-white/40 mt-4">
                  Make sure you have ArConnect extension installed
                </p>
              )}
            </div>
          )}

          {currentStep === 1 && (
            <div className="text-center">
              <MessageSquare className="w-16 h-16 mx-auto mb-4 text-green-500" />
              <h2 className="text-2xl font-bold mb-2">Link Your Discord Account</h2>
              <p className="text-white/60 mb-6">
                Connect your Discord account to verify your server membership
              </p>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={linkDiscord}
                disabled={loading || !guildId}
                className="px-8 py-3 bg-[#5865F2] hover:bg-[#4752C4] disabled:bg-gray-600 rounded-lg font-semibold transition-all flex items-center space-x-2 mx-auto"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Linking...</span>
                  </>
                ) : (
                  <>
                    <MessageSquare className="w-5 h-5" />
                    <span>Link Discord</span>
                  </>
                )}
              </motion.button>
              {!guildId && (
                <p className="text-sm text-red-400 mt-4">
                  Missing server context. Please start verification from a Discord server link.
                </p>
              )}
            </div>
          )}

          {currentStep === 2 && (
            <div className="text-center">
              <Link2 className="w-16 h-16 mx-auto mb-4 text-green-500" />
              <h2 className="text-2xl font-bold mb-2">Link Your Social Accounts</h2>
              <p className="text-white/60 mb-6">
                Optional: Connect your GitHub and X accounts for additional verification
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                {!githubId ? (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={linkGithub}
                    disabled={loading}
                    className="px-6 py-3 bg-gray-800 hover:bg-gray-700 disabled:bg-gray-600 rounded-lg font-semibold transition-all flex items-center space-x-2"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Linking...</span>
                      </>
                    ) : (
                      <>
                        <SiGithub className="w-5 h-5" />
                        <span>Link GitHub</span>
                      </>
                    )}
                  </motion.button>
                ) : (
                  <div className="px-6 py-3 bg-green-500/20 border border-green-500 rounded-lg flex items-center space-x-2">
                    <Check className="w-5 h-5 text-green-500" />
                    <span className="font-semibold">GitHub Linked</span>
                  </div>
                )}

                {!xId ? (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={linkX}
                    disabled={loading}
                    className="px-6 py-3 bg-[#1DA1F2] hover:bg-[#1a8cd8] disabled:bg-gray-600 rounded-lg font-semibold transition-all flex items-center space-x-2"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        <span>Linking...</span>
                      </>
                    ) : (
                      <>
                        <SiX className="w-5 h-5" />
                        <span>Link X</span>
                      </>
                    )}
                  </motion.button>
                ) : (
                  <div className="px-6 py-3 bg-green-500/20 border border-green-500 rounded-lg flex items-center space-x-2">
                    <Check className="w-5 h-5 text-green-500" />
                    <span className="font-semibold">X Linked</span>
                  </div>
                )}
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setCurrentStep(3)}
                className="mt-6 text-green-500 hover:text-green-400 transition-colors flex items-center space-x-2 mx-auto"
              >
                <span>Skip for now</span>
                <ChevronRight className="w-4 h-4" />
              </motion.button>
            </div>
          )}

          {currentStep === 3 && (
            <div className="text-center">
              <Shield className="w-16 h-16 mx-auto mb-4 text-green-500" />
              <h2 className="text-2xl font-bold mb-2">Verify Your Holdings</h2>
              <p className="text-white/60 mb-6">
                Complete the verification to prove your $SEND holdings
              </p>
              
              {/* Account Summary */}
              <div className="mb-6 p-4 bg-white/5 rounded-lg space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-white/60">Wallet:</span>
                  <span className="font-mono">{address.slice(0, 6)}...{address.slice(-4)}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-white/60">Discord:</span>
                  <span className="text-green-500">Connected</span>
                </div>
                {(githubId || xId) && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-white/60">Socials:</span>
                    <span className="text-green-500">
                      {[githubId && "GitHub", xId && "X"].filter(Boolean).join(", ")}
                    </span>
                  </div>
                )}
              </div>

              {/* ArNS Management Section - Simplified */}
              {(currentPrimaryName || antOptions.length > 0) && (
                <div className="mb-6 p-4 bg-white/5 rounded-lg">
                  <h4 className="text-sm font-medium text-white/80 mb-3">ArNS Management</h4>
                  
                  {currentPrimaryName ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-white/60">Current Primary Name:</span>
                        <span className="text-green-500 font-medium">{currentPrimaryName}</span>
                      </div>
                      {antOptions.length > 0 && (
                        <div className="text-sm text-white/60">
                          You have {antOptions.length} owned ANT{antOptions.length !== 1 ? 's' : ''} available
                        </div>
                      )}
                    </div>
                  ) : antOptions.length > 0 ? (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-white/60">Available ANTs:</span>
                        <span className="text-blue-400">{antOptions.length} owned</span>
                      </div>
                      <p className="text-sm text-white/60">
                        You can set a primary name after verification
                      </p>
                    </div>
                  ) : (
                    <div className="text-sm text-white/40">
                      No ArNS names found for this wallet
                    </div>
                  )}
                </div>
              )}

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={verify}
                disabled={loading || !guildId}
                className="px-8 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 disabled:from-gray-600 disabled:to-gray-700 rounded-lg font-semibold transition-all flex items-center space-x-2 mx-auto"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>Verifying...</span>
                  </>
                ) : (
                  <>
                    <Shield className="w-5 h-5" />
                    <span>Verify Holdings</span>
                  </>
                )}
              </motion.button>
              
              {!guildId && (
                <p className="text-sm text-red-400 mt-4">
                  Missing server context. Please start verification from a Discord server link.
                </p>
              )}
            </div>
          )}

          {/* Status Message */}
          {status && !error && (
            <p className="text-center text-sm text-white/60 mt-4">{status}</p>
          )}
        </motion.div>
      </main>

      {/* Success Modal */}
      <AnimatePresence>
        {showSuccess && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-gradient-to-br from-green-900/90 to-black/90 rounded-2xl p-8 max-w-md w-full border border-green-500/30"
            >
              <div className="text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring" as const, damping: 10 }}
                  className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4"
                >
                  <Check className="w-10 h-10 text-black" />
                </motion.div>
                <h3 className="text-2xl font-bold mb-2">Verification Complete!</h3>
                <p className="text-white/60 mb-4">
                  Your wallet has been verified and linked successfully.
                </p>
                {lastVerifiedRoles && lastVerifiedRoles.length > 0 && (
                  <div className="mb-6 p-4 bg-white/5 rounded-lg">
                    <p className="text-sm text-white/60 mb-2">Your Cosmic Roles:</p>
                    <p className="font-semibold text-green-500">
                      {lastVerifiedRoles.join(", ")}
                    </p>
                  </div>
                )}
                <div className="flex gap-3 justify-center">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowSuccess(false)}
                    className="px-6 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    Close
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={disconnectWallet}
                    className="px-6 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                  >
                    Disconnect
                  </motion.button>
                </div>
              </div>
            </motion.div>
            <Confetti
              width={window.innerWidth}
              height={window.innerHeight}
              recycle={false}
              numberOfPieces={200}
              colors={['#10b981', '#34d399', '#6ee7b7', '#ffffff']}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Profile Modal */}
      <AnimatePresence>
        {showProfile && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-6"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-[#012b12] text-white relative overflow-hidden rounded-2xl p-4 w-[75vw] h-screen overflow-y-auto border border-green-500/30"
            >
              <div className="flex justify-end mb-4 sticky top-0 z-10 ">
                <button
                  onClick={() => setShowProfile(false)}
                  className="text-white/60 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <Profile
                address={address}
                walletConnected={walletConnected}
                linkGithub={linkGithub}
                linkX={linkX}
                linkDiscord={linkDiscord}
                onDiscordIdFound={(id) => {
                  setDiscordId(id);
                  localStorage.setItem("discordId", id);
                }}
                onFetchUserError={(msg) => setError(msg)}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default AmVerify;